import type { AdminWorkspace, PublicWorkspace, WorkspaceListPage, WorkspaceSummary } from '../types'
import { BAY_PATH_ADMIN_WORKSPACE, BAY_PATH_PUBLIC_WORKSPACE } from './bay-path'
import {
  CODING_TEMPLE_ADMIN_WORKSPACE,
  CODING_TEMPLE_PUBLIC_ID,
  CODING_TEMPLE_PUBLIC_WORKSPACE,
} from './coding-temple'
import {
  NEXT_DEMO_ADMIN_WORKSPACE,
  NEXT_DEMO_PUBLIC_ID,
  NEXT_DEMO_PUBLIC_WORKSPACE,
} from './next-demo'
import {
  SUCCESS_DEMO_ADMIN_WORKSPACE,
  SUCCESS_DEMO_PUBLIC_ID,
  SUCCESS_DEMO_PUBLIC_WORKSPACE,
} from './success-demo'
import { SUCCESS_RENEWAL_DEMO_PUBLIC_ID, SUCCESS_RENEWAL_DEMO_PUBLIC_WORKSPACE } from './success-renewal-demo'

const PUBLIC_PREVIEWS: Record<string, PublicWorkspace> = {
  [BAY_PATH_PUBLIC_WORKSPACE.publicId]: BAY_PATH_PUBLIC_WORKSPACE,
  [CODING_TEMPLE_PUBLIC_ID]: CODING_TEMPLE_PUBLIC_WORKSPACE,
  [NEXT_DEMO_PUBLIC_ID]: NEXT_DEMO_PUBLIC_WORKSPACE,
  [SUCCESS_DEMO_PUBLIC_ID]: SUCCESS_DEMO_PUBLIC_WORKSPACE,
  [SUCCESS_RENEWAL_DEMO_PUBLIC_ID]: SUCCESS_RENEWAL_DEMO_PUBLIC_WORKSPACE,
}

const ADMIN_PREVIEWS: Record<string, AdminWorkspace> = {
  [BAY_PATH_ADMIN_WORKSPACE.publicId]: BAY_PATH_ADMIN_WORKSPACE,
  [CODING_TEMPLE_PUBLIC_ID]: CODING_TEMPLE_ADMIN_WORKSPACE,
  [NEXT_DEMO_PUBLIC_ID]: NEXT_DEMO_ADMIN_WORKSPACE,
  [SUCCESS_DEMO_PUBLIC_ID]: SUCCESS_DEMO_ADMIN_WORKSPACE,
}

export function publicPreviewById(id: string): PublicWorkspace | null {
  return PUBLIC_PREVIEWS[id] || null
}

export function adminPreviewById(id: string): AdminWorkspace | null {
  return ADMIN_PREVIEWS[id] || null
}

// Additional list-only rows so the local admin list preview reads like the
// real ~12-workspace roster instead of just the three fully-detailed demo
// accounts. These are display data for the list page only; they are not
// wired to full AdminWorkspace records, so their rows do not open a detail
// page in local preview mode.
const EXTRA_LIST_PREVIEWS: WorkspaceSummary[] = [
  { id: 'preview-devry', publicId: 'devry-university', accountName: 'DeVry University', phase: 'customer_success', status: 'published', pricingModel: 'enrollment_roi', accountLogoUrl: null, createdAt: '2025-11-03T14:00:00.000Z', updatedAt: '2026-08-24T19:05:00.000Z', lastActivityAt: '2026-08-24T18:40:00.000Z' },
  { id: 'preview-wgu', publicId: 'wgu-partnership', accountName: 'Western Governors University', phase: 'customer_success', status: 'published', pricingModel: 'enrollment_roi', accountLogoUrl: null, createdAt: '2026-01-14T16:30:00.000Z', updatedAt: '2026-08-23T13:12:00.000Z', lastActivityAt: '2026-08-22T09:15:00.000Z' },
  { id: 'preview-fvt', publicId: 'first-vertical-tech', accountName: 'First Vertical Technical College', phase: 'onboarding', status: 'published', pricingModel: 'enrollment_roi', accountLogoUrl: null, createdAt: '2026-06-02T12:00:00.000Z', updatedAt: '2026-08-21T10:45:00.000Z', lastActivityAt: null },
  { id: 'preview-isil', publicId: 'isil-lima', accountName: 'ISIL Lima', phase: 'sales', status: 'draft', pricingModel: null, accountLogoUrl: null, createdAt: '2026-08-05T09:00:00.000Z', updatedAt: '2026-08-20T17:22:00.000Z', lastActivityAt: null },
  { id: 'preview-breakthrough', publicId: 'break-through-tech', accountName: 'Break Through Tech', phase: 'onboarding', status: 'published', pricingModel: 'enrollment_roi', accountLogoUrl: null, createdAt: '2026-03-18T15:20:00.000Z', updatedAt: '2026-08-19T21:30:00.000Z', lastActivityAt: '2026-08-19T20:58:00.000Z' },
  { id: 'preview-goodwin', publicId: 'goodwin-university', accountName: 'Goodwin University', phase: 'sales', status: 'draft', pricingModel: null, accountLogoUrl: null, createdAt: '2026-08-11T11:10:00.000Z', updatedAt: '2026-08-18T08:05:00.000Z', lastActivityAt: null },
  { id: 'preview-cecu', publicId: 'cecu-network', accountName: 'CECU Member Network', phase: 'sales', status: 'draft', pricingModel: null, accountLogoUrl: null, createdAt: '2026-07-29T13:45:00.000Z', updatedAt: '2026-08-15T12:00:00.000Z', lastActivityAt: null },
  { id: 'preview-purdue', publicId: 'purdue-global', accountName: 'Purdue Global', phase: 'customer_success', status: 'published', pricingModel: 'enrollment_roi', accountLogoUrl: null, createdAt: '2025-09-22T10:00:00.000Z', updatedAt: '2026-08-10T15:40:00.000Z', lastActivityAt: '2026-08-09T16:02:00.000Z' },
  { id: 'preview-strayer', publicId: 'strayer-university', accountName: 'Strayer University', phase: 'onboarding', status: 'published', pricingModel: 'enrollment_roi', accountLogoUrl: null, createdAt: '2026-04-07T09:30:00.000Z', updatedAt: '2026-07-30T18:12:00.000Z', lastActivityAt: null },
]

export function previewSummaries(params: { page?: number; pageSize?: number; search?: string; status?: 'draft' | 'published' } = {}): WorkspaceListPage {
  const page = params.page && params.page > 0 ? params.page : 1
  const pageSize = params.pageSize && params.pageSize > 0 ? params.pageSize : 25
  const search = (params.search || '').trim().toLowerCase()

  const all: WorkspaceSummary[] = [
    ...Object.values(ADMIN_PREVIEWS).map((workspace) => ({
      id: workspace.id,
      publicId: workspace.publicId,
      accountName: workspace.accountName,
      phase: workspace.phase,
      status: workspace.status,
      pricingModel: workspace.pricingModel,
      accountLogoUrl: workspace.accountLogoUrl,
      createdAt: '2026-05-01T12:00:00.000Z',
      updatedAt: workspace.updatedAt,
      lastActivityAt: workspace.updatedAt,
    })),
    ...EXTRA_LIST_PREVIEWS,
  ].sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())

  const filtered = all
    .filter((workspace) => !search || workspace.accountName.toLowerCase().includes(search))
    .filter((workspace) => !params.status || workspace.status === params.status)
  const start = (page - 1) * pageSize
  return {
    workspaces: filtered.slice(start, start + pageSize),
    total: filtered.length,
    page,
    pageSize,
  }
}
